import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Folder, File, Download, Archive } from "lucide-react";
import PageWrapper from "../../components/PageWrapper";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import axiosClient from "../../services/axiosClient";

const S3FileManagement = () => {
    const { t } = useTranslation("general");
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [currentPath, setCurrentPath] = useState("");
    const [bucket, setBucket] = useState("");
    const [urlInput, setUrlInput] = useState("");
    const [history, setHistory] = useState([]);
    const [error, setError] = useState(null);

    // Authentication fields
    const [accessKeyId, setAccessKeyId] = useState("");
    const [secretAccessKey, setSecretAccessKey] = useState("");
    const [region, setRegion] = useState("us-east-1");

    const fetchItems = async (tgtBucket, tgtPrefix) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosClient.get("/api/s3/list", {
                params: {
                    bucket: tgtBucket,
                    prefix: tgtPrefix,
                    accessKeyId,
                    secretAccessKey,
                    region,
                },
            });
            setItems(response.data.items || []);
            setCurrentPath(response.data.currentPath || tgtPrefix || "");
            if (response.data.bucket) setBucket(response.data.bucket);
        } catch (err) {
            console.error("Error fetching S3 items:", err);
            setError(t("failedToLoadDirectory") || "Failed to load directory. Check bucket and permissions.");
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleNavigate = (path) => {
        setHistory([...history, { bucket, prefix: currentPath }]);
        fetchItems(bucket, path);
    };

    const handleGoBack = () => {
        if (history.length === 0) return;
        const previous = history[history.length - 1];
        setHistory(history.slice(0, -1));
        fetchItems(previous.bucket, previous.prefix);
    };

    const handleSearch = () => {
        let targetBucket = urlInput;
        let targetPrefix = "";

        if (urlInput.startsWith("s3://")) {
            const parts = urlInput.replace("s3://", "").split("/");
            targetBucket = parts[0];
            targetPrefix = parts.slice(1).join("/");
            if (targetPrefix && !targetPrefix.endsWith("/")) targetPrefix += "/";
        }

        setBucket(targetBucket);
        setHistory([]);
        fetchItems(targetBucket, targetPrefix);
    };

    const downloadFile = async (item) => {
        try {
            const response = await axiosClient.get("/api/s3/download", {
                params: {
                    bucket,
                    prefix: item.path,
                    accessKeyId,
                    secretAccessKey,
                    region,
                },
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", item.name);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error("Download failed:", err);
            alert(t("downloadFailed"));
        }
    };

    const downloadFolderZip = async (item) => {
        try {
            const response = await axiosClient.get("/api/s3/zip", {
                params: {
                    bucket,
                    prefix: item.path,
                    accessKeyId,
                    secretAccessKey,
                    region,
                },
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${item.name.replace(/\/$/, "")}.zip`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error("Zip download failed:", err);
            alert(t("failedToCreateZip"));
        }
    };

    return (
        <PageWrapper  title="s3FileManager">
            <div className="u-padding_400">
                {/* Authentication Section */}
                <div className="o-row o-row--gap-size_200 u-margin--bottom_400" style={{ alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                        <CustomInput
                            label={t("accessKeyId")}
                            placeholder={t("accessKeyIdPlaceholder")}
                            value={accessKeyId}
                            onChange={(e) => setAccessKeyId(e.target.value)}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <CustomInput
                            label={t("secretAccessKey")}
                            placeholder={t("secretAccessKeyPlaceholder")}
                            type="password"
                            value={secretAccessKey}
                            onChange={(e) => setSecretAccessKey(e.target.value)}
                        />
                    </div>
                    <div style={{ flex: 0.5 }}>
                        <CustomInput
                            label={t("awsRegion")}
                            placeholder="me-south-1"
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                        />
                    </div>
                </div>

                {/* Search / Config Section */}
                <div className="o-row o-row--gap-size_200 u-margin--bottom_400" style={{ alignItems: 'flex-end' }}>
                    <CustomButton
                        variant="blank"
                        onClick={handleGoBack}
                        disabled={history.length === 0 || loading}
                        icon="back"
                        title={t("back")}
                    />
                    <div style={{ flex: 1 }}>
                        <CustomInput
                            label={t("bucketNameOrUrl")}
                            placeholder={t("bucketPlaceholder")}
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                        />
                    </div>
                    <CustomButton
                        title={t("go")}
                        onClick={handleSearch}
                        disabled={!urlInput || !accessKeyId || !secretAccessKey || loading}
                        icon="search"
                    />
                </div>

                {/* Controls & Path */}
                <div className="o-row o-row--gap-size_200 u-margin--bottom_200" style={{ alignItems: "center" }}>
                    <span className="t-text-size_small u-color_gray-dark">
                        {t("currentPath")}: <strong>{bucket ? `s3://${bucket}/${currentPath}` : t("noBucketSelected")}</strong>
                    </span>
                </div>

                {/* Content Table */}
                {loading ? (
                    <div className="u-text-align_center u-padding_500">
                        <Loader />
                    </div>
                ) : error ? (
                    <div className="u-color_red-base u-text-align_center u-padding_400">{error}</div>
                ) : (
                    items.length > 0 ? (
                        <div style={{ overflowX: "auto" }}>
                            <table className="i-table" style={{ width: "100%" }}>
                                <thead>
                                    <tr className="u-border--bottom_2_gray">
                                        <th className="u-padding_300 t-text-align_left">{t("type")}</th>
                                        <th className="u-padding_300 t-text-align_left">{t("name")}</th>
                                        <th className="u-padding_300 t-text-align_center">{t("theSize")}</th>
                                        <th className="u-padding_300 t-text-align_center">{t("lastModified")}</th>
                                        <th className="u-padding_300 t-text-align_center">{t("Actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, idx) => (
                                        <tr key={idx} className="u-border--bottom_1_gray-ligth">
                                            <td className="u-padding_300">
                                                {item.type === "folder" ? (
                                                    <Folder size={20} className="u-color_secondary-base" />
                                                ) : (
                                                    <File size={20} className="u-color_gray-dark" />
                                                )}
                                            </td>
                                            <td className="u-padding_300">
                                                {item.type === "folder" ? (
                                                    <span
                                                        onClick={() => handleNavigate(item.path)}
                                                        style={{ cursor: "pointer", fontWeight: "bold", textDecoration: "underline" }}
                                                        className="u-color_primary-base"
                                                    >
                                                        {item.name}
                                                    </span>
                                                ) : (
                                                    item.name
                                                )}
                                            </td>
                                            <td className="u-padding_300 t-text-align_center">
                                                {item.size ? (item.size / 1024).toFixed(2) + " KB" : "-"}
                                            </td>
                                            <td className="u-padding_300 t-text-align_center">
                                                {item.lastModified ? new Date(item.lastModified).toLocaleDateString() : "-"}
                                            </td>
                                            <td className="u-padding_300 t-text-align_center">
                                                {item.type === "folder" ? (
                                                    <CustomButton
                                                        variant="blank"
                                                        content={<Archive size={16} />}
                                                        onClick={() => downloadFolderZip(item)}
                                                    // icon="archive" // Optional if reg has it
                                                    />
                                                ) : (
                                                    <CustomButton
                                                        variant="blank"
                                                        content={<Download size={16} />}
                                                        onClick={() => downloadFile(item)}
                                                    // icon="upload" // Optional
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        bucket && <div className="u-text-align_center u-padding_400">{t("noItemsFound")}</div>
                    )
                )}
            </div>
        </PageWrapper>
    );
};

export default S3FileManagement;
